import { useState } from 'react';
import jsPDF from 'jspdf';
import { formatCurrency } from '@sailpoint/bva-core'

import {
	FONT_FAMILY_NAME as poppinsFamily,
	FONT_VARIANT_NAME as lightVariant,
	FONT_FILENAME_FOR_VFS as lightFilename,
	FONT_BASE64_DATA as poppinsLightBase64
} from '../libs/assets/fonts/Poppins-light.js';

import {
	FONT_VARIANT_NAME as mediumVariant,
	FONT_FILENAME_FOR_VFS as mediumFilename,
	FONT_BASE64_DATA as poppinsMediumBase64
} from '../libs/assets/fonts/Poppins-medium.js';

// --- Configuration Constants ---
const PAGE_MARGIN_X = 18;
const PAGE_EDGE_TOP_MARGIN = 2;
const HEADER_BAR_HEIGHT = 15;
const LOGO_HEIGHT_MM = 12;
const SAILPOINT_LOGO_HEADER_ASPECT = 0.299;
const HERO_SECTION_HEIGHT = 65;
const SAILPOINT_BLUE_HERO_BG = '#0A209D';
const TEXT_WHITE = '#FFFFFF';
const LINE_SPACE_POP_MEDIUM_28PT = 10.7;
const LINE_SPACE_POP_LIGHT_14PT = 5.7;
//const LINE_SPACING_BETWEEN_LINES_MM = 4;
const LINE_SPACE_POP_MEDIUM_18PT = 7.0;
const V_SPACING_AFTER_SECTION = 15;
const PAGE_BREAK_TOP_MARGIN = PAGE_EDGE_TOP_MARGIN + HEADER_BAR_HEIGHT;
const ADDITIONAL_PADDING_ON_NEW_PAGE = 10;
const PAGE_BREAK_BOTTOM_THRESHOLD = 20;
const TABLE_HEADER_FONT_SIZE = 9;
const TABLE_ROW_FONT_SIZE = 9;
const TABLE_ROW_LINE_HEIGHT = 4;
const TABLE_CELL_PADDING_X = 2;
const TABLE_CELL_PADDING_Y = 3;
const TABLE_HEADER_HEIGHT = 11;
const PAGE_NUMBER_FONT_SIZE = 9;
const PAGE_NUMBER_MARGIN_BOTTOM = 10;
const PAGE_NUMBER_MARGIN_RIGHT = 15;
const colors = ['#0033a1', '#0071CE', '#54C0E8', '#00B294', '#93D500', '#F2A900', '#FF5F00', '#E62E2E', '#8A3FFC', '#d60eb5', '#CC27B0', '#33B1FF', '#415364', '#718096', '#A0AEC0'];


let cachedSailpointLogoHeaderDataUrl = null;
// Placeholder utility for imageUrlToBase64
//async function imageUrlToBase64(url) {
// You should implement this or use a library
//    return '';
//}

function drawPageNumbers(doc) {
	const pageCount = doc.internal.getNumberOfPages();
	const originalPage = doc.internal.getCurrentPageInfo().pageNumber;
	doc.setFont(poppinsFamily, lightVariant).setFontSize(PAGE_NUMBER_FONT_SIZE).setTextColor('#415364');
	for (let i = 1; i <= pageCount; i++) {
		doc.setPage(i);
		const text = `Page ${i} of ${pageCount}`;
		const x = doc.internal.pageSize.width - PAGE_NUMBER_MARGIN_RIGHT;
		const y = doc.internal.pageSize.height - PAGE_NUMBER_MARGIN_BOTTOM;
		doc.text(text, x, y, { align: 'right' });
	}
	doc.setPage(originalPage);
}

function managePageBreaks(doc, currentY, contentHeightNeeded, headerLogoDataUrl) {
	const pageHeight = doc.internal.pageSize.height;
	const spaceRemaining = pageHeight - currentY - PAGE_BREAK_BOTTOM_THRESHOLD;
	if (contentHeightNeeded > spaceRemaining) {
		doc.addPage();
		if (headerLogoDataUrl) {
			// Match page 1 logo placement: preserve aspect ratio and move further left
			const logoHeight = LOGO_HEIGHT_MM;
			const logoWidth = logoHeight / SAILPOINT_LOGO_HEADER_ASPECT;
			const logoY = PAGE_EDGE_TOP_MARGIN + (HEADER_BAR_HEIGHT - logoHeight) / 2;
			const logoX = PAGE_MARGIN_X - 12;
			doc.addImage(headerLogoDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
		}
		return PAGE_BREAK_TOP_MARGIN + ADDITIONAL_PADDING_ON_NEW_PAGE;
	}
	return currentY;
}

function formatPercentage(value) {
	if (value === null || value === 0) return '-';
	return `${value}%`;
}

const ExportPdfButton = ({ results, donutCanvasElement, barCanvasElement, BENEFIT_VALUE_PROPOSITIONS }) => {
	const [isExporting, setIsExporting] = useState(false);
	// You may want to use a notification system for errors/success


	async function handleExportPdf() {
		if (!results || !donutCanvasElement || !barCanvasElement) {
			return;
		}
		setIsExporting(true);
		//logErrorMessage(null);
		//logSuccessMessage('Generating PDF...');

		try {
			//if (!cachedSailpointLogoHeaderDataUrl) {
			//	cachedSailpointLogoHeaderDataUrl = await imageUrlToBase64('/sailpoint.png');
			//}
			const donutImageDataUrl = donutCanvasElement.toDataURL('image/png', 1.0);
			const barImageDataUrl = barCanvasElement.toDataURL('image/png', 1.0);

			const doc = new jsPDF('l', 'mm', 'a4');
			doc.addFileToVFS(lightFilename, poppinsLightBase64);
			doc.addFont(lightFilename, poppinsFamily, lightVariant);
			doc.addFileToVFS(mediumFilename, poppinsMediumBase64);
			doc.addFont(mediumFilename, poppinsFamily, mediumVariant);

			const pageWidth = doc.internal.pageSize.width;
			let currentY = PAGE_EDGE_TOP_MARGIN;

			// --- PAGE 1: Header and Hero ---
			//if (cachedSailpointLogoHeaderDataUrl) {
			// Ensure aspect ratio is preserved and move logo further left
			//	const logoHeight = LOGO_HEIGHT_MM;
			//	const logoWidth = logoHeight / SAILPOINT_LOGO_HEADER_ASPECT;
			//	const logoY = currentY + (HEADER_BAR_HEIGHT - logoHeight) / 2;
			//	const logoX = PAGE_MARGIN_X - 12; // Move logo 12 units further left
			//	doc.addImage(cachedSailpointLogoHeaderDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
			//}
			currentY += HEADER_BAR_HEIGHT;

			doc.setFillColor(SAILPOINT_BLUE_HERO_BG).rect(0, currentY, pageWidth, HERO_SECTION_HEIGHT, 'F');

			// Only two lines now: Benefits Analysis and account name
			const totalTextBlockHeight = LINE_SPACE_POP_MEDIUM_28PT + LINE_SPACE_POP_LIGHT_14PT + 2; // 2 is the extra vertical space
			const textBlockStartY = currentY + (HERO_SECTION_HEIGHT - totalTextBlockHeight) / 2;
			const line1_baseline = textBlockStartY + (LINE_SPACE_POP_MEDIUM_28PT * 0.8);
			//const line2_baseline = line1_baseline + LINE_SPACE_POP_LIGHT_14PT + 2;
			// const line3_baseline = line2_baseline + LINE_SPACE_POP_LIGHT_14PT;

			doc.setFont(poppinsFamily, mediumVariant).setFontSize(28).setTextColor(TEXT_WHITE);
			doc.text('Benefits Analysis', PAGE_MARGIN_X, line1_baseline);
			doc.setFont(poppinsFamily, lightVariant).setFontSize(14);
			//doc.text(selectedOpp.account_name, PAGE_MARGIN_X + 1, line2_baseline); // Decrease left padding
			// doc.text(selectedOpp.opportunity_name, PAGE_MARGIN_X, line3_baseline);

			currentY += HERO_SECTION_HEIGHT + 25;

			const donutTitleText = "5-Year Savings Breakdown";
			const barTitleText = "Total Savings per Year";
			const donutImageAspect = donutCanvasElement.height / donutCanvasElement.width;
			const barImageAspect = barCanvasElement.height / barCanvasElement.width;
			const CHART_DONUT_CANVAS_PDF_WIDTH = 70;
			const MAX_ALLOWED_BAR_CHART_PDF_WIDTH = 120;
			const H_SPACING_DONUT_LEGEND = 10;
			const SPACING_BETWEEN_TWO_CHARTS_H = 17;
			const CHART_SECTION_EDGE_PADDING = 15;
			const donutPdfHeight_calculated = CHART_DONUT_CANVAS_PDF_WIDTH * donutImageAspect;
			const tempDoc = new jsPDF();

			// ** MODIFIED: Use smaller font for measuring legend text **
			tempDoc.setFont(poppinsFamily, 'light').setFontSize(8);
			const maxLegendTextWidth = Math.max(...results.donutChartData.map(item => tempDoc.getTextWidth(item.benefit)));

			const legendCircleRadius = 1.5;
			const legendLineHeight = 4.5; // Tighter spacing for smaller font
			const calculatedLegendHeight_total = results.donutChartData.length * legendLineHeight;
			const legendItemEffectiveWidth = (legendCircleRadius * 2) + 5 + maxLegendTextWidth;

			let barPdfHeight_actual = donutPdfHeight_calculated;
			let barPdfWidth_actual = barPdfHeight_actual / barImageAspect;
			if (barPdfWidth_actual > MAX_ALLOWED_BAR_CHART_PDF_WIDTH) {
				barPdfWidth_actual = MAX_ALLOWED_BAR_CHART_PDF_WIDTH;
				barPdfHeight_actual = barPdfWidth_actual * barImageAspect;
			}

			const intrinsicDonutBlockWidth = CHART_DONUT_CANVAS_PDF_WIDTH + H_SPACING_DONUT_LEGEND + legendItemEffectiveWidth;
			const intrinsicBarBlockWidth = barPdfWidth_actual;
			const totalChartsSectionRequiredWidth = CHART_SECTION_EDGE_PADDING + intrinsicDonutBlockWidth + SPACING_BETWEEN_TWO_CHARTS_H + intrinsicBarBlockWidth + CHART_SECTION_EDGE_PADDING;
			const chartsSectionStartX = (pageWidth - totalChartsSectionRequiredWidth) / 2;
			const donutColumnDrawStartX = chartsSectionStartX + CHART_SECTION_EDGE_PADDING;
			const barColumnDrawStartX = chartsSectionStartX + CHART_SECTION_EDGE_PADDING + intrinsicDonutBlockWidth + SPACING_BETWEEN_TWO_CHARTS_H;
			const chartsRowCurrentY = currentY;
			const titleLineHeightSpacing = LINE_SPACE_POP_MEDIUM_18PT + 2;
			const chartsImagesContentStartGlobalY = chartsRowCurrentY + titleLineHeightSpacing;
			const commonVerticalContentHeight = Math.max(donutPdfHeight_calculated, calculatedLegendHeight_total, barPdfHeight_actual);
			doc.setFont(poppinsFamily, 'medium').setFontSize(18).setTextColor('#000000');
			doc.text(donutTitleText, donutColumnDrawStartX + (intrinsicDonutBlockWidth / 2), chartsRowCurrentY, { align: 'center' });
			const donutImageAdjustedY = chartsImagesContentStartGlobalY + (commonVerticalContentHeight - donutPdfHeight_calculated) / 2;
			doc.addImage(donutImageDataUrl, 'PNG', donutColumnDrawStartX, donutImageAdjustedY, CHART_DONUT_CANVAS_PDF_WIDTH, donutPdfHeight_calculated);

			// ** MODIFIED: Use smaller font for drawing legend text **
			doc.setFont(poppinsFamily, 'light').setFontSize(8).setTextColor('#415364');
			const legendAdjustedY = chartsImagesContentStartGlobalY + (commonVerticalContentHeight - calculatedLegendHeight_total) / 2;
			let legendXPositionForDrawing = donutColumnDrawStartX + CHART_DONUT_CANVAS_PDF_WIDTH + H_SPACING_DONUT_LEGEND;
			let legendCurrentYDrawingPosition = legendAdjustedY;
			results.donutChartData.forEach((item, index) => {
				const color = colors[index % colors.length];
				doc.setFillColor(color).circle(legendXPositionForDrawing + legendCircleRadius, legendCurrentYDrawingPosition - legendCircleRadius / 2, legendCircleRadius, 'F');
				doc.text(item.benefit, legendXPositionForDrawing + (legendCircleRadius * 2) + 5, legendCurrentYDrawingPosition);
				legendCurrentYDrawingPosition += legendLineHeight;
			});

			doc.setFont(poppinsFamily, 'medium').setFontSize(18).setTextColor('#000000');
			doc.text(barTitleText, barColumnDrawStartX + (intrinsicBarBlockWidth / 2), chartsRowCurrentY, { align: 'center' });
			const barImageAdjustedY = chartsImagesContentStartGlobalY + (commonVerticalContentHeight - barPdfHeight_actual) / 2;
			doc.addImage(barImageDataUrl, 'PNG', barColumnDrawStartX, barImageAdjustedY, barPdfWidth_actual, barPdfHeight_actual);
			currentY = chartsImagesContentStartGlobalY + commonVerticalContentHeight + V_SPACING_AFTER_SECTION;

			// --- Benefit Table Pagination & Rendering ---
			const sortedPositiveBenefits = results.savingsBreakdown.filter(row => row.total > 0 && row.benefit !== 'Total').sort((a, b) => b.total - a.total);
			let benefitChunks = [];
			const totalBenefits = sortedPositiveBenefits.length;
			const MAX_BENEFITS_PER_SLIDE = 8;
			const MIN_BENEFITS_ON_LAST_SLIDE = 3;
			if (totalBenefits > 0) {
				if (totalBenefits <= MAX_BENEFITS_PER_SLIDE) {
					benefitChunks.push(sortedPositiveBenefits);
				} else {
					const remainder = totalBenefits % MAX_BENEFITS_PER_SLIDE;
					const numPages = Math.ceil(totalBenefits / MAX_BENEFITS_PER_SLIDE);
					if (numPages === 2 && remainder > 0 && remainder < MIN_BENEFITS_ON_LAST_SLIDE) {
						const splitPoint = Math.ceil(totalBenefits / 2);
						benefitChunks.push(sortedPositiveBenefits.slice(0, splitPoint));
						benefitChunks.push(sortedPositiveBenefits.slice(splitPoint));
					} else {
						for (let i = 0; i < totalBenefits; i += MAX_BENEFITS_PER_SLIDE) {
							benefitChunks.push(sortedPositiveBenefits.slice(i, i + MAX_BENEFITS_PER_SLIDE));
						}
					}
				}
			}
			if (benefitChunks.length > 0) {
				currentY = doc.internal.pageSize.height;
				const pageAvailableWidth = pageWidth - 2 * PAGE_MARGIN_X;
				const benefitTableCols = [
					{ header: 'Benefit', width: 50 },
					{ header: 'How Does SailPoint add Value', width: pageAvailableWidth - 50 - 35 },
					{ header: 'Total', width: 35 }
				];
				benefitChunks.forEach((chunk, index) => {
					currentY = managePageBreaks(doc, currentY, 50, cachedSailpointLogoHeaderDataUrl);
					let slideTitle = 'Key Value Drivers for Identity Security Cloud';
					if (benefitChunks.length > 1) slideTitle += ` (${index + 1}/${benefitChunks.length})`;
					doc.setFont(poppinsFamily, 'medium').setFontSize(18).setTextColor('#000000');
					doc.text(slideTitle, pageWidth / 2, currentY, { align: 'center' });
					currentY += LINE_SPACE_POP_MEDIUM_18PT + 5;
					let headerY = currentY;
					doc.setFont(poppinsFamily, 'light').setFontSize(TABLE_HEADER_FONT_SIZE).setTextColor('#415364');
					let currentX = PAGE_MARGIN_X;
					benefitTableCols.forEach((col, idx) => {
						if (col.header === 'Total') {
							doc.text(col.header, currentX + col.width - TABLE_CELL_PADDING_X, headerY + TABLE_HEADER_HEIGHT - TABLE_CELL_PADDING_Y, { align: 'right' });
						} else {
							doc.text(col.header, currentX + TABLE_CELL_PADDING_X, headerY + TABLE_HEADER_HEIGHT - TABLE_CELL_PADDING_Y);
						}
						currentX += col.width;
					});
					doc.line(PAGE_MARGIN_X, headerY + TABLE_HEADER_HEIGHT, pageWidth - PAGE_MARGIN_X, headerY + TABLE_HEADER_HEIGHT);
					currentY += TABLE_HEADER_HEIGHT;
					doc.setFont(poppinsFamily, 'light').setFontSize(TABLE_ROW_FONT_SIZE).setTextColor('#415364');
					chunk.forEach(row => {
						const valuePropText = BENEFIT_VALUE_PROPOSITIONS[row.benefit] || '';
						const wrappedValueText = doc.splitTextToSize(valuePropText, benefitTableCols[1].width - 2 * TABLE_CELL_PADDING_X);
						const wrappedBenefitText = doc.splitTextToSize(row.benefit, benefitTableCols[0].width - 2 * TABLE_CELL_PADDING_X);
						const rowHeight = Math.max(wrappedBenefitText.length, wrappedValueText.length) * TABLE_ROW_LINE_HEIGHT + 2 * TABLE_CELL_PADDING_Y;
						currentY = managePageBreaks(doc, currentY, rowHeight, cachedSailpointLogoHeaderDataUrl);
						const col1_x = PAGE_MARGIN_X + TABLE_CELL_PADDING_X;
						const col2_x = PAGE_MARGIN_X + benefitTableCols[0].width + TABLE_CELL_PADDING_X;
						const col3_x = PAGE_MARGIN_X + benefitTableCols[0].width + benefitTableCols[1].width + benefitTableCols[2].width - TABLE_CELL_PADDING_X;
						doc.text(wrappedBenefitText, col1_x, currentY + TABLE_CELL_PADDING_Y + 2);
						doc.text(wrappedValueText, col2_x, currentY + TABLE_CELL_PADDING_Y + 2);
						doc.text(formatCurrency(row.total, results.currencySymbol, 'en-US', true), col3_x, currentY + TABLE_CELL_PADDING_Y + 2, { align: 'right' });
						currentY += rowHeight;
						doc.setDrawColor('#E5E7EB'); // Tailwind gray-200
						doc.line(PAGE_MARGIN_X, currentY, pageWidth - PAGE_MARGIN_X, currentY);
						doc.setDrawColor('#415364'); // Reset to default after
					});
					currentY += V_SPACING_AFTER_SECTION + 10; // Add extra space between tables
				});
			}

			// --- Detailed Table Rendering ---
			const detailedTableData = results.savingsBreakdown.filter(row => row.total > 0 || row.benefit === 'Total').sort((a, b) => (a.benefit === 'Total' ? 1 : b.benefit === 'Total' ? -1 : b.total - a.total));
			if (detailedTableData.length > 0) {
				currentY = managePageBreaks(doc, currentY, 50, cachedSailpointLogoHeaderDataUrl);
				doc.setFont(poppinsFamily, 'medium').setFontSize(18).setTextColor('#000000');
				doc.text("Detailed Savings Breakdown", pageWidth / 2, currentY, { align: 'center' });
				currentY += LINE_SPACE_POP_MEDIUM_18PT + 5;
				const detailedCols = [
					{ header: "Benefit", width: 80 }, { header: "Year 1", width: 25 }, { header: "Year 2", width: 25 },
					{ header: "Year 3", width: 25 }, { header: "Year 4", width: 25 }, { header: "Year 5", width: 25 },
					{ header: "Total", width: 30 }, { header: "% Improvement", width: 30 }
				];
				const detailedTableWidth = detailedCols.reduce((sum, col) => sum + col.width, 0);
				const detailedTableStartX = (pageWidth - detailedTableWidth) / 2;
				let headerY = currentY;
				doc.setFont(poppinsFamily, 'light').setFontSize(TABLE_HEADER_FONT_SIZE).setTextColor('#415364');
				let x = detailedTableStartX;
				detailedCols.forEach((col, idx) => {
					if (idx === 0) {
						doc.text(col.header, x + TABLE_CELL_PADDING_X, headerY + TABLE_HEADER_HEIGHT - TABLE_CELL_PADDING_Y, { align: 'left' });
					} else {
						doc.text(col.header, x + col.width - TABLE_CELL_PADDING_X, headerY + TABLE_HEADER_HEIGHT - TABLE_CELL_PADDING_Y, { align: 'right' });
					}
					x += col.width;
				});
				doc.line(detailedTableStartX, headerY + TABLE_HEADER_HEIGHT, detailedTableStartX + detailedTableWidth, headerY + TABLE_HEADER_HEIGHT);
				currentY += TABLE_HEADER_HEIGHT;
				doc.setFont(poppinsFamily, 'light').setFontSize(TABLE_ROW_FONT_SIZE).setTextColor('#415364');
				detailedTableData.forEach(row => {
					const rowHeight = 12;
					currentY = managePageBreaks(doc, currentY, rowHeight, cachedSailpointLogoHeaderDataUrl);
					let cellX = detailedTableStartX;
					const cellY = currentY + TABLE_CELL_PADDING_Y + 2;
					// Benefit left-aligned
					doc.text(row.benefit, cellX + TABLE_CELL_PADDING_X, cellY, { align: 'left' }); cellX += detailedCols[0].width;
					// All other columns right-aligned
					doc.text(formatCurrency(row.y1, results.currencySymbol), cellX + detailedCols[1].width - TABLE_CELL_PADDING_X, cellY, { align: 'right' }); cellX += detailedCols[1].width;
					doc.text(formatCurrency(row.y2, results.currencySymbol), cellX + detailedCols[2].width - TABLE_CELL_PADDING_X, cellY, { align: 'right' }); cellX += detailedCols[2].width;
					doc.text(formatCurrency(row.y3, results.currencySymbol), cellX + detailedCols[3].width - TABLE_CELL_PADDING_X, cellY, { align: 'right' }); cellX += detailedCols[3].width;
					doc.text(formatCurrency(row.y4, results.currencySymbol), cellX + detailedCols[4].width - TABLE_CELL_PADDING_X, cellY, { align: 'right' }); cellX += detailedCols[4].width;
					doc.text(formatCurrency(row.y5, results.currencySymbol), cellX + detailedCols[5].width - TABLE_CELL_PADDING_X, cellY, { align: 'right' }); cellX += detailedCols[5].width;
					doc.text(formatCurrency(row.total, results.currencySymbol), cellX + detailedCols[6].width - TABLE_CELL_PADDING_X, cellY, { align: 'right' }); cellX += detailedCols[6].width;
					doc.text(formatPercentage(row.percentageImprovement), cellX + detailedCols[7].width - TABLE_CELL_PADDING_X, cellY, { align: 'right' }); cellX += detailedCols[7].width;
					currentY += rowHeight;
					doc.setDrawColor('#E5E7EB'); // Tailwind gray-200
					doc.line(detailedTableStartX, currentY, detailedTableStartX + detailedTableWidth, currentY);
					doc.setDrawColor('#415364'); // Reset to default after
				});
			}

			// --- Finalize and Save PDF ---
			const lastPage = doc.internal.getCurrentPageInfo().pageNumber;
			drawPageNumbers(doc);
			doc.setPage(lastPage);

			const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
			const filename = `BVA_Analysis_${timestamp}.pdf`;
			doc.save(filename);
			//logSuccessMessage.set('PDF generated successfully!');
		} catch (err) {
			//const message = err instanceof Error ? err.message : 'An unknown error occurred.';
			console.error('PDF Export Error:', err);
			//logErrorMessage.set(`Failed to generate PDF: ${message}`);
		} finally {
			setIsExporting(false);
			setTimeout(() => { }, 5000);
		}
	}

	return (
		<button
			onClick={handleExportPdf}
			disabled={isExporting || !results || !donutCanvasElement || !barCanvasElement}
			className="relative cursor-pointer px-6 py-2 w-3xs rounded-md shadow text-white font-semibold overflow-hidden bg-gradient-to-r from-blue-900 to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 group"
		>
			<span className="absolute inset-0 bg-gradient-to-r from-[#d60eb5] to-[#f06dff] opacity-0 transition-opacity duration-300 ease-in-out z-10 group-hover:opacity-100"></span>
			<span className="relative z-20">
				{isExporting ? 'Exporting...' : 'Download PDF'}
			</span>
		</button>
	);

};

export default ExportPdfButton;
